import { useCallback, useEffect, useMemo, useRef, type Ref } from "react";
import { Box, CircularProgress, Typography, type SxProps, type Theme } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import { isLibraryDragAllowedForRestriction, type LibraryDragMediaRestriction } from "@/utils/dragTransfer";
import type { FavoriteMediaKind } from "@/services/project-entity";
import LibraryImageCard from "./LibraryImageCard";
import type { LibraryGridEntry, LibraryItem } from "./libraryItems";

/** One logical grid row: either a full-width "Previous breakdown" divider, or up to GRID_COLUMNS cards. */
type GridRow =
  | { readonly kind: "divider"; readonly id: string; readonly label: string }
  | { readonly kind: "items"; readonly items: readonly LibraryItem[] };

/** A divider always starts a fresh row and occupies it alone (never shares a row with cards). */
function chunkEntriesIntoRows(entries: readonly LibraryGridEntry[], columns: number): GridRow[] {
  const rows: GridRow[] = [];
  let currentRowItems: LibraryItem[] = [];

  const flushRow = () => {
    if (currentRowItems.length > 0) {
      rows.push({ kind: "items", items: currentRowItems });
      currentRowItems = [];
    }
  };

  for (const entry of entries) {
    if (entry.kind === "divider") {
      flushRow();
      rows.push({ kind: "divider", id: entry.id, label: entry.label });
      continue;
    }

    currentRowItems.push(entry.item);
    if (currentRowItems.length === columns) {
      flushRow();
    }
  }

  flushRow();
  return rows;
}

/** "Previous breakdown — Aug 13, 2026" section label above the assets a past regen still references. */
function LibraryGroupDivider({ label, sx }: { readonly label: string; readonly sx?: SxProps<Theme> }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: "block",
        color: "text.secondary",
        fontWeight: 600,
        pt: 1.5,
        pb: 0.75,
        ...sx
      }}
    >
      {label}
    </Typography>
  );
}

export interface LibraryGridProps {
  readonly projectId: string | null;
  readonly entries: readonly LibraryGridEntry[];
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly completedVideoByImageId: ReadonlyMap<string, string>;
  readonly libraryDragRestriction: LibraryDragMediaRestriction | null;
  /** Owner-lock gate: when false, cards can't be dragged out or double-clicked into the main space. */
  readonly canWrite: boolean;
  readonly currentMainspaceImageId: string | null;
  readonly highlightedLibraryItemId: string | null;
  readonly highlightCardRef: Ref<HTMLDivElement>;
  readonly draggingImageId: string | null;
  readonly draggingLibraryVideoId: string | null;
  readonly onSelect: (imageId: string, videoId?: string) => void;
  readonly onDragStart: (imageId: string, libraryVideoId: string | null) => void;
  readonly onDragEnd: () => void;
  readonly onPlayVideo: (videoId: string) => void;
  readonly hasMoreToLoad?: boolean;
  readonly onLoadMore?: () => void;
  readonly onToggleFavorite?: (mediaId: string, kind: FavoriteMediaKind) => void;
}

const GRID_COLUMNS = 2;
const GRID_GAP = 1.5;

/**
 * Above this item count, only rows near the viewport are mounted instead of every card - "View All"
 * can list 100s of images/videos and mounting every LibraryImageCard at once (each with its own
 * useImageEntity fetch, ResizeObserver, and drag listeners) gets sluggish. Below the threshold the
 * plain grid is simpler and sidesteps virtualization edge cases for the common small lists.
 */
const VIRTUALIZE_ITEM_THRESHOLD = 40;
const ESTIMATED_ROW_HEIGHT_PX = 160;
const VIRTUAL_ROW_OVERSCAN = 6;
const LOAD_MORE_ROW_THRESHOLD = 4;

function LibraryGrid({
  projectId,
  entries,
  isLoading,
  isError,
  completedVideoByImageId,
  libraryDragRestriction,
  canWrite,
  currentMainspaceImageId,
  highlightedLibraryItemId,
  highlightCardRef,
  draggingImageId,
  draggingLibraryVideoId,
  onSelect,
  onDragStart,
  onDragEnd,
  onPlayVideo,
  hasMoreToLoad = false,
  onLoadMore,
  onToggleFavorite
}: LibraryGridProps) {
  const scrollElementRef = useRef<HTMLDivElement | null>(null);
  const gridRows = useMemo(() => chunkEntriesIntoRows(entries, GRID_COLUMNS), [entries]);
  const shouldVirtualize = !isLoading && entries.length > VIRTUALIZE_ITEM_THRESHOLD;
  const rowCount = gridRows.length;

  const rowVirtualizer = useVirtualizer({
    count: shouldVirtualize ? rowCount : 0,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => ESTIMATED_ROW_HEIGHT_PX,
    overscan: VIRTUAL_ROW_OVERSCAN
  });

  const highlightedRowIndex = useMemo(() => {
    if (highlightedLibraryItemId === null) {
      return null;
    }

    const index = gridRows.findIndex((row) => row.kind === "items" && row.items.some((item) => item.id === highlightedLibraryItemId));
    return index === -1 ? null : index;
  }, [gridRows, highlightedLibraryItemId]);

  useEffect(() => {
    if (!shouldVirtualize || highlightedRowIndex === null) {
      return;
    }

    // Instant jump so the target row is mounted well before useLibraryData's own smooth
    // scrollIntoView (fired ~300ms later via highlightCardRef) has to find it in the DOM.
    rowVirtualizer.scrollToIndex(highlightedRowIndex, { align: "center" });
  }, [shouldVirtualize, highlightedRowIndex, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const lastVisibleRowIndex = virtualRows.at(-1)?.index ?? -1;

  useEffect(() => {
    if (!hasMoreToLoad || onLoadMore === undefined) {
      return;
    }

    if (!shouldVirtualize) {
      onLoadMore();
      return;
    }

    if (lastVisibleRowIndex !== -1 && lastVisibleRowIndex >= rowCount - LOAD_MORE_ROW_THRESHOLD) {
      onLoadMore();
    }
  }, [hasMoreToLoad, onLoadMore, shouldVirtualize, lastVisibleRowIndex, rowCount]);

  const handleCardDragStart = useCallback(
    (imageId: string, libraryVideoId?: string | null) => {
      onDragStart(imageId, libraryVideoId ?? null);
    },
    [onDragStart]
  );

  const renderCard = useCallback(
    (item: LibraryItem) => {
      const cardVideoId = item.libraryVideoId ?? (item.section === "shots" ? (completedVideoByImageId.get(item.imageId) ?? null) : null);
      const placementAllowed =
        canWrite && (libraryDragRestriction === null || isLibraryDragAllowedForRestriction(libraryDragRestriction, cardVideoId));
      const isHighlighted = item.id === highlightedLibraryItemId;
      return (
        <LibraryImageCard
          key={item.id}
          projectId={projectId}
          item={item}
          dragEnabled={placementAllowed}
          onSelect={onSelect}
          isSelected={currentMainspaceImageId !== null && item.imageId === currentMainspaceImageId}
          isHighlighted={isHighlighted}
          highlightRef={isHighlighted ? highlightCardRef : undefined}
          isDragging={draggingImageId === item.imageId && (draggingLibraryVideoId ?? null) === (cardVideoId ?? null)}
          onDragStart={handleCardDragStart}
          onDragEnd={onDragEnd}
          videoId={cardVideoId}
          onPlayVideo={onPlayVideo}
          onToggleFavorite={onToggleFavorite}
        />
      );
    },
    [
      completedVideoByImageId,
      libraryDragRestriction,
      canWrite,
      highlightedLibraryItemId,
      projectId,
      onSelect,
      currentMainspaceImageId,
      highlightCardRef,
      draggingImageId,
      draggingLibraryVideoId,
      handleCardDragStart,
      onDragEnd,
      onPlayVideo,
      onToggleFavorite
    ]
  );

  return (
    <Box
      ref={scrollElementRef}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        px: 2,
        pb: 1,
        backgroundColor: "background.paper"
      }}
    >
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : null}
      {isError ? (
        <Typography variant="body2" color="error" sx={{ py: 2, textAlign: "center" }}>
          Failed to load project media.
        </Typography>
      ) : null}
      {!isLoading && shouldVirtualize ? (
        <Box sx={{ position: "relative", width: "100%", height: rowVirtualizer.getTotalSize() }}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = gridRows[virtualRow.index];
            if (row === undefined) {
              return null;
            }

            return (
              <Box
                key={virtualRow.key}
                ref={rowVirtualizer.measureElement}
                data-index={virtualRow.index}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualRow.start}px)`,
                  pb: GRID_GAP
                }}
              >
                {row.kind === "divider" ? (
                  <LibraryGroupDivider label={row.label} />
                ) : (
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: GRID_GAP }}>
                    {row.items.map((item) => renderCard(item))}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      ) : null}
      {!isLoading && !shouldVirtualize ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: GRID_GAP
          }}
        >
          {entries.map((entry) => {
            return entry.kind === "divider" ? (
              <LibraryGroupDivider key={entry.id} label={entry.label} sx={{ gridColumn: "1 / -1" }} />
            ) : (
              renderCard(entry.item)
            );
          })}
        </Box>
      ) : null}
      {!isLoading && entries.length === 0 ? (
        <Typography variant="body2" sx={{ color: "text.secondary", py: 2, textAlign: "center" }}>
          No assets to show
        </Typography>
      ) : null}
    </Box>
  );
}

export default LibraryGrid;
